import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  selectStationAssignmentData,
  setSelectedHalf,
} from '../../store/slices/stationAssignmentSlice';
import { ROUTES } from '../../utils/constants';
import StationAssignmentTabs from '../StationAssignmentTabs';
import ShiftRosterPage from '../../pages/stationAssignment/shiftRoster';
import PlannedHeadcountPage from '../../pages/stationAssignment/plannedHeadcount';
import AssignmentsPage from '../../pages/stationAssignment/assignments';

/**
 * StationAssignmentLayout component that provides persistent tabs and nested routing
 * for the station assignment workflow. Reads date/shift/half from Redux store.
 * Mirrors the ShiftPlanLayout pattern.
 */
const StationAssignmentLayout: React.FC = () => {
  const dispatch = useAppDispatch();

  // Get station assignment data from Redux store
  const { selectedDate, selectedShift, selectedHalf, isStationAssignmentActive } =
    useAppSelector(selectStationAssignmentData);

  // If no station assignment is active, redirect to the station assignment landing page.
  if (!isStationAssignmentActive || !selectedDate || !selectedShift) {
    return <Navigate to={ROUTES.SHIFT_ROTATION} replace />;
  }

  const handleHalfChange = (half: string) => {
    dispatch(setSelectedHalf(half));
  };

  // The first tab now defaults to the new Planned Headcount screen across all
  // environments (it has replaced the legacy Shift Roster screen).
  const defaultTabPath = 'planned-headcount';

  return (
    <StationAssignmentTabs
      date={selectedDate}
      shiftType={selectedShift}
      selectedHalf={selectedHalf ?? 'firstHalfStationAssignment'}
      onHalfChange={handleHalfChange}
    >
      <Routes>
        {/* Default route - redirect to the environment-specific first tab */}
        <Route path="/" element={<Navigate to={defaultTabPath} replace />} />

        {/* Tab content routes */}
        <Route path="/shift-roster" element={<ShiftRosterPage />} />
        <Route path="/planned-headcount" element={<PlannedHeadcountPage />} />
        <Route path="/assignments" element={<AssignmentsPage />} />

        {/* Fallback route */}
        <Route path="*" element={<Navigate to={defaultTabPath} replace />} />
      </Routes>
    </StationAssignmentTabs>
  );
};

export default StationAssignmentLayout;
